import { createContext, useContext, useEffect, useRef, useState } from "react";
import { syncStatusBar, listenForAppResume } from "@/lib/native";
import { initAdMob, showAppOpenAd } from "@/lib/admob";

const ThemeCtx = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem("stylio-theme") || "light";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("stylio-theme", theme);
    // Keep the Android status bar tint in sync with the app theme.
    // Runs only on native — no-op in web preview.
    syncStatusBar(theme);
  }, [theme]);

  // AdMob boot — runs once on mount. Web is a no-op inside `initAdMob`.
  useEffect(() => {
    initAdMob().catch(() => {});
  }, []);

  // App Open ad on resume — throttled to at most once every 5 minutes so
  // returning users aren't ad-spammed. Skips the *very first* activation
  // (app cold start already showed the launch screen). Native-only.
  const lastAppOpenRef = useRef(0);
  const skipFirstRef = useRef(true);
  useEffect(() => {
    const MIN_MS = 5 * 60 * 1000;
    let cleanup;
    listenForAppResume(() => {
      if (skipFirstRef.current) { skipFirstRef.current = false; return; }
      const now = Date.now();
      if (now - lastAppOpenRef.current < MIN_MS) return;
      lastAppOpenRef.current = now;
      showAppOpenAd().catch(() => {});
    }).then((c) => { cleanup = c; });
    return () => { cleanup?.(); };
  }, []);

  const toggle = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  return (
    <ThemeCtx.Provider value={{ theme, toggle }}>{children}</ThemeCtx.Provider>
  );
}

export const useTheme = () => useContext(ThemeCtx);
